/**
 * Carding Production Form — Layer 2.
 *
 * The headline page in Layer 2. Operator types ONLY:
 *   • actual_output_kg
 *   • total_waste_kg (optional)
 *
 * Layer 1 spine then auto-creates the Cans, computes cost, ranks
 * achievement vs forecast, and links the Production Log to whichever
 * laydown the FIFO walker attributes it to.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, App, Button, Card, Col, Form, Input, InputNumber, Row, Select,
  Space, Tag, Typography,
} from 'antd'
import { SaveOutlined, RocketOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import PageHeader from '@/shared/components/PageHeader'
import { productionLogsApi } from '@/features/reports/api'
import { cardProductionApi, cansApi } from '@/features/baleManagement/api'
import { ReadOnlyTile, GeneratedCansSummary } from './_shared'
import { useOperationsContext } from './_context'

const { Text } = Typography

export default function CardingProductionPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [plantId, setPlantId] = useState<string | undefined>()
  const [machineId, setMachineId] = useState<string | undefined>()
  const [shiftId, setShiftId] = useState<string | undefined>()
  const [savedLogId, setSavedLogId] = useState<string | null>(null)
  const [actualOutput, setActualOutput] = useState<number | null>(null)

  const today = dayjs().format('YYYY-MM-DD')

  // Sprint 0074 review — auto-context: plant + shift + card machines +
  // active laydown all pre-resolved. Operator picks the card and types.
  const ctx = useOperationsContext({ preferredPlantId: plantId })
  const { plantOptions, defaultPlantId, shifts, currentShift,
          cardMachines, activeLaydown } = ctx

  useEffect(() => { if (!plantId && defaultPlantId) setPlantId(defaultPlantId) }, [defaultPlantId])
  useEffect(() => { if (!shiftId && currentShift?.id) setShiftId(currentShift.id) }, [currentShift?.id])

  // Card production forecast — drives the "standard expected" tile
  // and lets us show production_difference_kg + achievement %.
  const { data: forecast } = useQuery({
    queryKey: ['card-forecast', plantId, machineId, actualOutput],
    queryFn: () => cardProductionApi.forecast({
      plant_id: plantId!,
      machine_id: machineId,
      running_hours: 8,
      actual_output_kg: actualOutput ?? undefined,
    }),
    enabled: !!plantId && !!machineId,
  })

  // Recent CD logs for duplicate-warning + ranking sneak-peek.
  const { data: recentLogs = [] } = useQuery({
    queryKey: ['cd-prod-recent', plantId, today],
    queryFn: () => productionLogsApi.listLogs({
      plant_id: plantId!, department_code: 'CARDING',
    }),
    enabled: !!plantId,
  })

  const dupRow = useMemo(() => {
    if (!plantId || !machineId || !shiftId) return null
    return (recentLogs || []).find((l: any) =>
      l.machine_id === machineId
      && l.shift_id === shiftId
      && l.log_date === today
    )
  }, [recentLogs, plantId, machineId, shiftId, today])

  // After a save, fetch the cans the spine just generated for this log.
  const { data: generatedCans = [], refetch: refetchCans } = useQuery({
    queryKey: ['cans-by-log', savedLogId],
    queryFn: () => cansApi.list({ production_log_id: savedLogId }),
    enabled: !!savedLogId,
  })

  const saveMutation = useMutation({
    mutationFn: (payload: any) => productionLogsApi.createLog(payload),
    onSuccess: async (saved: any) => {
      setSavedLogId(saved.id)
      message.success(`Production log saved — auto-generating cans…`)
      // Allow a short tick for the can-generation hook to commit, then
      // re-fetch. The hook is synchronous so this is normally instant.
      setTimeout(() => refetchCans(), 200)
      qc.invalidateQueries({ queryKey: ['cd-prod-recent'] })
      form.resetFields(['actual_output_kg', 'total_waste_kg', 'remarks'])
      setActualOutput(null)
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Save failed'),
  })

  useEffect(() => {
    form.setFieldsValue({ log_date: today })
  }, [form, today])

  const onSubmit = async () => {
    const v = await form.validateFields()
    if (!plantId || !machineId || !shiftId) {
      message.error('Plant, card machine + shift are required')
      return
    }
    saveMutation.mutate({
      plant_id: plantId,
      machine_id: machineId,
      shift_id: shiftId,
      log_date: today,
      department_code: 'CARDING',
      actual_output_kg: v.actual_output_kg,
      total_waste_kg: v.total_waste_kg ?? null,
      total_stoppage_minutes: v.total_stoppage_minutes ?? null,
      remarks: v.remarks || null,
    })
  }

  const fc = forecast as any
  const comp = fc?.comparison

  return (
    <div>
      <PageHeader
        title="Carding — Production"
        subtitle="Type output kg + waste kg. Cans, cost, efficiency, achievement % — all auto."
      />
      <Row gutter={16}>
        <Col xs={24} lg={14}>
          <Card>
            <Form layout="vertical" form={form} size="large">
              <Row gutter={12}>
                <Col xs={24} md={8}>
                  <Form.Item label="Plant" required>
                    <Select options={plantOptions} value={plantId} onChange={setPlantId} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Card Machine" required>
                    <Select
                      options={cardMachines.map((m: any) => ({
                        label: `${m.machine_id_str} — ${m.name}`, value: m.id,
                      }))}
                      value={machineId} onChange={setMachineId}
                      placeholder={cardMachines.length ? 'Select card' : 'Add cards in Masters'}
                      disabled={!plantId} showSearch optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Shift" required>
                    <Select
                      options={(shifts || []).map((s: any) => ({
                        label: s.shift_name || s.shift_code, value: s.id,
                      }))}
                      value={shiftId} onChange={setShiftId}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {dupRow && (
                <Alert
                  type="warning" showIcon style={{ marginBottom: 12 }}
                  message="A log already exists for this card + shift today"
                  description="A new save will be a separate row. Edit the existing log if you meant to update."
                />
              )}

              <Row gutter={12}>
                <Col xs={12}>
                  <Form.Item
                    name="actual_output_kg" label="Actual output (kg)"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }} step={1} min={0} inputMode="decimal"
                      onChange={v => setActualOutput(typeof v === 'number' ? v : null)}
                      placeholder="e.g. 425"
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item name="total_waste_kg" label="Waste kg (optional)">
                    <InputNumber style={{ width: '100%' }} step={1} min={0} inputMode="decimal" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col xs={12}>
                  <Form.Item name="total_stoppage_minutes" label="Stop minutes (if any)">
                    <InputNumber style={{ width: '100%' }} min={0} step={5} />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item name="remarks" label="Remarks">
                    <Input placeholder="Only if exception" />
                  </Form.Item>
                </Col>
              </Row>

              <Button
                type="primary" size="large" block icon={<SaveOutlined />}
                loading={saveMutation.isPending} onClick={onSubmit}
              >
                Save & Auto-generate cans
              </Button>
            </Form>
          </Card>

          {/* Generated cans summary appears here after save. */}
          <GeneratedCansSummary cans={(generatedCans as any[]).map((c: any) => ({
            can_no: c.can_no,
            net_kg: c.net_kg,
            is_partial_can: c.is_partial_can,
            qc_status: c.qc_status,
            total_can_cost: c.total_can_cost ?? null,
          }))} />
        </Col>

        <Col xs={24} lg={10}>
          <Card title={<><RocketOutlined /> Linked context (auto)</>}>
            <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
              <Col span={12}>
                <ReadOnlyTile
                  label="Active laydown"
                  value={activeLaydown ? activeLaydown.laydown_no : 'None'}
                  hint={activeLaydown
                    ? `${activeLaydown.count_description || ''} · ${activeLaydown.status}`
                    : 'Start a laydown in Bale Management → Planning'}
                  source={activeLaydown ? 'SYSTEM' : 'MANUAL'}
                />
              </Col>
              <Col span={12}>
                <ReadOnlyTile
                  label="Current shift"
                  value={currentShift
                    ? `${currentShift.shift_name} (${currentShift.start_time?.slice(0,5)}–${currentShift.end_time?.slice(0,5)})`
                    : '—'}
                  hint={currentShift ? 'Picked from now' : 'No shift covers now'}
                  source={currentShift ? 'AUTO' : 'MANUAL'}
                />
              </Col>
              <Col span={12}>
                <ReadOnlyTile
                  label="Cards available"
                  value={`${cardMachines.length} card(s)`}
                  hint="Filtered by section = CARDING"
                  source={cardMachines.length > 0 ? 'AUTO' : 'MANUAL'}
                />
              </Col>
              <Col span={12}>
                <ReadOnlyTile
                  label="FIFO laydown attribution"
                  value="auto"
                  hint="Production log auto-attributes to oldest pending laydown"
                  source="SYSTEM"
                />
              </Col>
            </Row>
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <ReadOnlyTile
                  label="Card sliver hank"
                  value={fc?.card_sliver_hank ?? '—'}
                  hint={`Source: ${fc?.source || '—'}`}
                />
              </Col>
              <Col span={12}>
                <ReadOnlyTile
                  label="Delivery speed"
                  value={fc ? `${fc.delivery_speed_m_min} m/min` : '—'}
                />
              </Col>
              <Col span={12}>
                <ReadOnlyTile
                  label="Sliver weight"
                  value={fc ? `${fc.sliver_weight_g_per_m} g/m` : '—'}
                  source="SYSTEM"
                />
              </Col>
              <Col span={12}>
                <ReadOnlyTile
                  label="Theoretical kg/hr"
                  value={fc ? Number(fc.theoretical_kg_hr).toFixed(2) : '—'}
                  source="SYSTEM"
                />
              </Col>
              <Col span={24}>
                <ReadOnlyTile
                  label="Standard expected (8h shift)"
                  value={fc ? `${Number(fc.shift_production_kg).toFixed(2)} kg` : '—'}
                  hint="@ standard efficiency"
                  source="SYSTEM"
                />
              </Col>
              {comp && (
                <>
                  <Col span={12}>
                    <ReadOnlyTile
                      label="Production diff"
                      value={`${Number(comp.production_difference_kg) >= 0 ? '+' : ''}${Number(comp.production_difference_kg).toFixed(2)} kg`}
                      source="SYSTEM"
                    />
                  </Col>
                  <Col span={12}>
                    <ReadOnlyTile
                      label="Achievement"
                      value={
                        <Tag color={
                          Number(comp.production_achievement_pct) >= 100 ? 'green'
                          : Number(comp.production_achievement_pct) >= 90 ? 'gold' : 'orange'
                        } style={{ fontSize: 14 }}>
                          {Number(comp.production_achievement_pct).toFixed(1)}%
                        </Tag>
                      }
                      source="SYSTEM"
                    />
                  </Col>
                </>
              )}
              <Col span={12}>
                <ReadOnlyTile
                  label="Std can kg"
                  value={fc ? `${fc.standard_can_kg} kg` : '25 kg'}
                />
              </Col>
              <Col span={12}>
                <ReadOnlyTile
                  label="Std waste %"
                  value={fc ? `${fc.standard_waste_pct}%` : '4%'}
                />
              </Col>
            </Row>
          </Card>

          <Card title="What auto-flows after save" style={{ marginTop: 12 }}>
            <Space direction="vertical">
              <Text style={{ fontSize: 12 }}>
                ✓ Cans generated automatically with C-{'{'}CARD{'}'}-{'{'}YYMMDD{'}'}-{'{'}SHIFT{'}'}-{'{'}SEQ{'}'} numbering
              </Text>
              <Text style={{ fontSize: 12 }}>
                ✓ Lot, mixing, BR line, laydown stamped from active laydown (FIFO)
              </Text>
              <Text style={{ fontSize: 12 }}>
                ✓ Cost stack (RM + process + waste) computed per can
              </Text>
              <Text style={{ fontSize: 12 }}>
                ✓ Efficiency auto-recomputed from breakdown logs
              </Text>
              <Text style={{ fontSize: 12 }}>
                ✓ All cans default to QC <Tag>PENDING</Tag> — QC officer flips them in bulk
              </Text>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  )
}
